import { createSignal, type Accessor } from 'solid-js'
import { walkObjects, parseFontFamily } from '../canvas/utils'
import { resolveEffectiveStyle, type ThemeRecord } from '../project/themeVariables'
import type { RuntimeObject } from '../types'

export const BUILTIN_FONTS = [
	'Roboto:wght@400;700;900',
	'Inter:wght@400;700',
	'Oswald:wght@400;700',
	'Anton',
	'Lora:wght@400;700',
]

export interface ParsedFont {
	spec: string
	family: string
	weights: number[]
}

export function parseSpec(spec: string): ParsedFont {
	const [namePart, wghtPart] = spec.split(':')
	const family  = namePart.replace(/\+/g, ' ')
	const weights = wghtPart
		? wghtPart.replace(/^wght@/, '').split(';').map(Number).filter(Boolean)
		: [400]
	return { spec, family, weights }
}

export const BUILTIN_PARSED   = BUILTIN_FONTS.map(parseSpec)
export const BUILTIN_FAMILIES = new Set(BUILTIN_PARSED.map(f => f.family.toLowerCase()))

/* The families the poster renders in, which are the ones to load and to wait on. It resolves the
   theme bindings for the same reason the renderer does: a bound fontFamily renders in the
   variable's family, and the object's stored one is free to differ (see resolveEffectiveStyle). */
export function collectUsedFamilies(objs: RuntimeObject[], theme: ThemeRecord|undefined): Set<string> {
	const used = new Set<string>()
	walkObjects(objs, obj => {
		if (obj.type !== 'image') {
			const ff = resolveEffectiveStyle(obj, theme)?.fontFamily
			if (typeof ff === 'string') used.add(parseFontFamily(ff))
		}
	})
	return used
}

// The Google Fonts URL out of a pasted embed code ("<link href=...>"), as the family specifiers
// it carries. Null when the text holds no such URL.
export function parseGoogleFontsEmbed(text: string): string[] | null {
	const match = text.match(/https:\/\/fonts\.googleapis\.com\/css2\?[^\s"'<]+/)
	if (!match) return null
	try {
		const url = new URL(match[0])
		return url.searchParams.getAll('family').filter(Boolean)
	} catch {
		return null
	}
}

export interface FontContext {
	fonts: Accessor<string[]>
	parsedFonts: () => ParsedFont[]
	preloadFamilies: Accessor<Set<string>>
	preloadFont: (family: string) => void
}

// The fonts a poster may use come from its theme (see PosterTheme's Fonts section) — a design
// carries none of its own, so this is read-only: it turns the theme's specifiers into the
// picker's list and tracks the families a control has asked to be preloaded.
export function createFontState(themeFonts: () => string[]): FontContext {
	const fonts: Accessor<string[]> = () => themeFonts()

	const parsedFonts = (): ParsedFont[] => [
		...BUILTIN_PARSED,
		...fonts().map(parseSpec).filter(f => !BUILTIN_FAMILIES.has(f.family.toLowerCase())),
	]

	const [preloadFamilies, setPreloadFamilies] = createSignal(new Set<string>())
	function preloadFont(family: string) {
		setPreloadFamilies(prev => { const s = new Set(prev); s.add(family); return s })
	}

	return { fonts, parsedFonts, preloadFamilies, preloadFont }
}
